import {useMemo} from "react";
import type {Hit} from "algoliasearch";
import {useConfigContext} from "@/ConfigProvider";
import {useAutocomplete} from "@/autocomplete/context/GlobalContext";
import {Panel} from "@/autocomplete/components/Panel";
import type {AlgoliaProductAttributes} from "@/AlgoliaProductAttributes";

const CATEGORY_LEVELS = ['lvl1', 'lvl2', 'lvl3'] as const;

interface HierarchicalCategoryEntry {
    name?: string;
    slug?: string;
}

type HitWithHierarchicalCategories = Hit<AlgoliaProductAttributes> & {
    hierarchicalCategories?: Partial<Record<typeof CATEGORY_LEVELS[number], HierarchicalCategoryEntry>>;
};

interface CategorySuggestion {
    category: string;
    slug: string;
}

// Extracted from the 'categories' source's raw product hits (see GlobalContext.tsx),
// deduplicated by category name/slug, capped to the first 10 found.
function extractCategories(hits: HitWithHierarchicalCategories[]): CategorySuggestion[] {
    const categoriesSet = new Set<string>();
    hits.forEach((hit) => {
        const hierarchicalCategories = hit.hierarchicalCategories;
        if (!hierarchicalCategories) return;
        CATEGORY_LEVELS.forEach((level) => {
            const category = hierarchicalCategories[level];
            if (category?.name && category?.slug) {
                categoriesSet.add(JSON.stringify({category: category.name, slug: category.slug}));
            }
        });
    });
    return Array.from(categoriesSet).map((item) => JSON.parse(item)).slice(0, 10);
}

export function Categories() {
    const {autocompleteState, autocomplete} = useAutocomplete();
    const {tc, algoliaConfig: {customizedUrl}} = useConfigContext();
    const {collections} = autocompleteState;
    const hits = useMemo(
        () => (collections.find(({source}) => source.sourceId === "categories")?.items || []) as HitWithHierarchicalCategories[],
        [collections]
    );
    const items = useMemo(() => extractCategories(hits), [hits]);
    if (items.length === 0) return <></>;
    return (
        <Panel title={tc.categories}>
            <ul className="algoliaCategories" {...autocomplete.getListProps()}>
                {items.map((item, itemIndex) => (
                    <li key={itemIndex}
                        className="algoliaCategories__item">
                        <a className="linkWithIcon" href={`/${customizedUrl}/${item.slug}`}>
                            <span>{item.category}</span></a>
                    </li>
                ))}
            </ul>
        </Panel>
    );
}
